/* Huffy: <huffy-variant-sync data-sync-id="…" data-product-id="…">
   Keeps variant-dependent markup that lives OUTSIDE Horizon's own re-rendered components (spec cells,
   sizing highlight, …) in step with the selected variant.

   How it works: Horizon's variant-picker refetches the whole product page for the new selection
   (it keeps ?view=pdp2) and dispatches the standard `shopify:product:select` event (bubbles to document)
   whose `promise` resolves with { detail: { html } }. Each <huffy-variant-sync> looks up its twin
   (same data-sync-id) in that response and copies the twin's markup and `hidden` state into itself.

   Rules for authors:
   - data-sync-id must be unique on the page (prefix it with section.id).
   - Render the element on every request (empty is fine) so the twin always exists in the response.
   - Content is replaced wholesale, so keep interactive state (e.g. an open <details>) outside it.

   Safe to connect/disconnect repeatedly: one document listener per connected instance, torn down on
   disconnect via AbortController; stale responses (rapid clicks) are ignored by a request token. */
import { StandardEvents } from '@shopify/events';

const PRODUCT_SELECT = StandardEvents?.productSelect || 'shopify:product:select';
// Variant pickers inside these fetch a different section (card / quick add / featured product), not this page.
const FOREIGN_SOURCES = 'product-card, quick-add-component, quick-add-dialog, featured-product-information, dialog';

class HuffyVariantSync extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  #token = 0;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    document.addEventListener(PRODUCT_SELECT, this.#onProductSelect, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = undefined;
    this.#token += 1; // drop any response still in flight
  }

  /** @param {Event & { product?: { id?: string }, promise?: Promise<any> }} event */
  #onProductSelect = (event) => {
    const source = event.target;
    if (!(source instanceof Element) || source.closest(FOREIGN_SOURCES)) return;

    const ownId = this.dataset.productId;
    const eventId = String(event.product?.id ?? '').split('/').pop();
    if (ownId && eventId && eventId !== ownId) return;
    if (!event.promise || typeof event.promise.then !== 'function') return;

    const token = ++this.#token;
    event.promise
      .then((result) => {
        if (token !== this.#token || !this.isConnected) return;
        const html = result?.detail?.html;
        if (html && typeof html.querySelector === 'function') this.sync(html);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[huffy-variant-sync]', error);
      });
  };

  /**
   * Copies the twin element's markup from a freshly rendered document.
   * @param {Document | Element} html
   */
  sync(html) {
    const key = this.dataset.syncId;
    if (!key) return;
    const twin = html.querySelector(`huffy-variant-sync[data-sync-id="${CSS.escape(key)}"]`);
    if (!(twin instanceof HTMLElement)) return;

    this.hidden = twin.hidden;
    if (twin.innerHTML === this.innerHTML) return;

    // Clone so other listeners reading the same response document still find their own nodes.
    const fresh = /** @type {HTMLElement} */ (twin.cloneNode(true));
    this.replaceChildren(...Array.from(fresh.childNodes));
    this.dispatchEvent(new CustomEvent('huffy:variant-sync', { bubbles: true, detail: { syncId: key } }));
  }
}

if (!customElements.get('huffy-variant-sync')) customElements.define('huffy-variant-sync', HuffyVariantSync);
